import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export interface StoredSession {
  id: string;
  preview: string;
  updatedAt: string;
}

interface State {
  sessions: StoredSession[];
}

interface Actions {
  upsertSession: (session: StoredSession) => void;
  removeSession: (id: string) => void;
  clearSessions: () => void;
}

export const useRecentConversationsStore = create<State & Actions>()(
  devtools(
    persist(
      (set) => ({
        sessions: [],

        upsertSession: (session) =>
          set(
            (s) => {
              const existing = s.sessions.find((e) => e.id === session.id);
              // Don't overwrite a good preview with an empty one
              if (existing?.preview && !session.preview) return s;
              // No-op if nothing changed (prevents re-render loops)
              if (
                existing &&
                existing.preview === session.preview &&
                existing.updatedAt === session.updatedAt
              )
                return s;
              return {
                sessions: [
                  { ...existing, ...session },
                  ...s.sessions.filter((e) => e.id !== session.id),
                ].slice(0, 20),
              };
            },
            false,
            'recentConversations/upsertSession'
          ),

        removeSession: (id) =>
          set(
            (s) => ({ sessions: s.sessions.filter((e) => e.id !== id) }),
            false,
            'recentConversations/removeSession'
          ),

        clearSessions: () =>
          set({ sessions: [] }, false, 'recentConversations/clear'),
      }),
      {
        name: 'mambaby-recent-conversations',
        version: 3,
        migrate: (persisted: unknown, version: number) => {
          if (!persisted || typeof persisted !== 'object')
            return { sessions: [] };
          if (version === 0) {
            // v0: { conversations: [{ sessionId, firstMessage?, ... }] }
            const old = persisted as {
              conversations?: Array<{
                sessionId: string;
                firstMessage?: string;
              }>;
            };
            return {
              sessions: (old.conversations ?? []).map((c) => ({
                id: c.sessionId,
                preview: c.firstMessage?.slice(0, 80) ?? '',
                updatedAt: new Date().toISOString(),
              })),
            };
          }
          if (version === 1) {
            // v1: { sessionIds: string[] }
            const old = persisted as { sessionIds?: string[] };
            return {
              sessions: (old.sessionIds ?? []).map((id) => ({
                id,
                preview: '',
                updatedAt: new Date().toISOString(),
              })),
            };
          }
          if (version === 2) {
            const old = persisted as State;
            return {
              sessions: old.sessions.filter((s) => s.preview.trim() !== ''),
            };
          }
          return persisted as State;
        },
      }
    ),
    { name: 'MamBaby/recentConversations' }
  )
);
